import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { TrendingUp, CheckCircle2 } from "lucide-react";
import CosmicBackground from "./CosmicBackground";

const Historien = () => {
  const { t } = useTranslation();
  const impactItems = t("historien.impact_items", { returnObjects: true });

  return (
    <section
      id="historien"
      data-testid="historien-section"
      className="relative section-pad overflow-hidden"
    >
      <CosmicBackground variant="rich" />

      <div className="relative mx-auto max-w-[1180px] px-5 md:px-10">
        <div className="text-center">
          <span className="eyebrow">{t("historien.eyebrow")}</span>
          <h2 className="font-display mt-5 text-xl sm:text-[1.5rem] lg:text-[1.75rem] font-semibold leading-[1.18] text-white glow-text-white uppercase tracking-[-0.005em]">
            {t("historien.title")}
          </h2>
        </div>

        {/* Story timeline — vertical with neon spine */}
        <div className="relative mt-16">
          <div
            aria-hidden
            className="absolute left-[14px] top-2 bottom-2 w-px md:left-1/2 md:-translate-x-1/2"
            style={{
              background:
                "linear-gradient(180deg, transparent, #FF2D5C 10%, #FF3DCB 35%, #9B4DFF 65%, #3DBFFF 90%, transparent)",
            }}
          />

          {[
            { year: "2017", text: t("historien.p1"), color: "#FF2D5C" },
            { year: "First season", text: t("historien.p2"), color: "#FF3DCB" },
            { year: "HUI · 6/6", text: t("historien.p3"), color: "#9B4DFF" },
            { year: "2025", text: t("historien.p4"), color: "#3DBFFF" },
          ].map((item, i) => (
            <motion.div
              key={i}
              data-testid={`historien-step-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={`relative grid gap-6 py-7 md:grid-cols-2 md:gap-12 ${
                i % 2 === 0 ? "md:[&>*:first-child]:order-1" : ""
              }`}
            >
              {/* Spine dot */}
              <span
                className="absolute left-[14px] top-9 h-3 w-3 -translate-x-1/2 rounded-full md:left-1/2"
                style={{
                  background: item.color,
                  boxShadow: `0 0 18px ${item.color}, 0 0 6px ${item.color}`,
                }}
              />

              <div className="pl-10 md:pl-0 md:pr-12">
                <span
                  className="font-mono text-[11px] uppercase tracking-[0.24em]"
                  style={{ color: item.color }}
                >
                  {item.year}
                </span>
              </div>
              <div className="pl-10 md:pl-12">
                <p className="text-base md:text-lg text-white/80 leading-relaxed">
                  {item.text}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Collective Flow ribbon */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="relative mt-20 overflow-hidden rounded-3xl electric-border"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,45,92,0.10) 0%, rgba(155,77,255,0.10) 50%, rgba(61,191,255,0.10) 100%)",
            backdropFilter: "blur(18px)",
          }}
        >
          <div
            aria-hidden
            className="absolute -left-24 -top-24 h-80 w-80 rounded-full opacity-50 blur-3xl pulse-glow"
            style={{ background: "#FF3DCB" }}
          />
          <div
            aria-hidden
            className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full opacity-50 blur-3xl pulse-glow"
            style={{ background: "#3DBFFF", animationDelay: "-2s" }}
          />

          <div className="relative px-7 py-12 md:px-14 md:py-16">
            <span className="eyebrow">{t("historien.ribbon_title")}</span>
            <p className="font-display mt-6 text-xl md:text-2xl text-white/85 leading-relaxed">
              {t("historien.ribbon_p1")}
            </p>
            <p className="mt-5 text-base md:text-lg text-white/75 leading-relaxed">
              {t("historien.ribbon_p2")}
              <span className="font-display headline-neon font-bold">
                {t("historien.ribbon_highlight")}
              </span>
              {t("historien.ribbon_p3")}
            </p>
          </div>
        </motion.div>

        {/* Impact — concrete effects of the method, no abstract percentages */}
        <div className="mt-16">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-5 w-5 text-[#6FF6FF]" />
            <span className="eyebrow">{t("historien.impact_title")}</span>
          </div>
          <p className="mt-4 max-w-2xl text-base md:text-lg text-white/75">
            {t("historien.impact_lead")}
          </p>
          <div className="mt-7 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {Array.isArray(impactItems) &&
              impactItems.map((it, i) => (
                <motion.div
                  key={i}
                  data-testid={`historien-impact-${i}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                  className="group flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.015] px-5 py-4 transition-colors hover:border-[#FF3DCB]/40"
                >
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FF3DCB]" />
                  <span className="font-display text-sm md:text-[15px] leading-snug text-white/90">
                    {it}
                  </span>
                </motion.div>
              ))}
          </div>

          <p className="mt-12 text-center font-display text-xl md:text-2xl font-semibold text-white">
            {t("historien.outro")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Historien;
